import { useMemo } from 'react';

import { useSellerSetting } from '../../../hooks/use-seller-setting';
import { SellerSettings } from '../../../enums/seller-settings/seller-settings';
import { getCurrentDateTimeAsISO, getDaysBetweenISODates } from '../../../services/datetime-service';
import { PLAN_CODE_TO_BILLING_PLAN_ID, PLAN_CODE_TO_TIER_NAME, type PlanTrialHistory, type PlanTrialInfo } from './plan-trial-constants';

type PlanTrialData = {
  isInPlanTrial: boolean;
  activePlanCode: string;
  activeBillingPlanId: number;
  daysLeftInPlanTrial: number;
  planTierName: string;
};

const DEFAULT_PLAN_TRIAL_DATA: PlanTrialData = {
  isInPlanTrial: false,
  activePlanCode: '',
  activeBillingPlanId: 0,
  daysLeftInPlanTrial: 0,
  planTierName: '',
};

/**
 * Hook to get plan trial info from the PlanFeaturesTrialInfo seller setting.
 * The setting stores a JSON dictionary keyed by plan code (e.g., "standard_2025q2", "premium_2025q2").
 */
export const useGetPlanTrialData = (): PlanTrialData => {
  const planTrialInfoJson = useSellerSetting(SellerSettings.planFeaturesTrialInfo);

  return useMemo(() => {
    if (!planTrialInfoJson) {
      return DEFAULT_PLAN_TRIAL_DATA;
    }

    try {
      const planTrialHistory: PlanTrialHistory = JSON.parse(planTrialInfoJson);
      const now = new Date();

      // Find the most recent active trial (where TrialEndUtc > now)
      let activePlanTrial: PlanTrialInfo | null = null;
      let activePlanCode: string | null = null;
      let latestTrialEndDate: Date | null = null;

      for (const [planCode, trialInfo] of Object.entries(planTrialHistory)) {
        const trialEndDate = new Date(trialInfo.TrialEndUtc);
        if (trialEndDate > now) {
          // If we have multiple active trials, use the one that ends latest
          if (!latestTrialEndDate || trialEndDate > latestTrialEndDate) {
            activePlanTrial = trialInfo;
            activePlanCode = planCode;
            latestTrialEndDate = trialEndDate;
          }
        }
      }

      if (!activePlanTrial) {
        return DEFAULT_PLAN_TRIAL_DATA;
      }

      const daysLeftInPlanTrial = getDaysBetweenISODates(getCurrentDateTimeAsISO(), activePlanTrial.TrialEndUtc) ?? 0;
      const planTierName = PLAN_CODE_TO_TIER_NAME[activePlanCode ?? ''] ?? '';

      return {
        isInPlanTrial: true,
        activePlanCode: activePlanCode ?? '',
        activeBillingPlanId: PLAN_CODE_TO_BILLING_PLAN_ID[activePlanCode ?? ''] ?? 0,
        daysLeftInPlanTrial,
        planTierName,
      };
    } catch {
      return DEFAULT_PLAN_TRIAL_DATA;
    }
  }, [planTrialInfoJson]);
};
